import {useParams} from "react-router-dom";
import React, {useContext, useEffect} from "react";
import {
  Box,
  Card,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import useFetchAllQuery, {useFetchAllQueryHelper} from "../../../hooks/useFetchAllQuery";
import {findObject} from "../../../utils/awsAmplifyUtil";
import {OPERATING_ENVIRONMENT_URLS} from "../../../constants/constants";
import {CONTEXT_ACTION_TYPE, DataStoreContext, ObjectDetailPayload} from "../contexts/OperatingEnvironmentContext";
import DownloadObjectFileButton from "../components/buttons/DownloadObjectFileButton";
import View3D, {ControlState, useControlState, useThree} from "../components/3d/View3D";
import {handleInputChange} from "../../../utils/formUtil";
import {MaterialTypeField} from "../components/3d/controlFields/MaterialTypeField";
import {PerspectiveField} from "../components/3d/controlFields/PerspectiveField";
import {LookAtField} from "../components/3d/controlFields/LookAtField";
import {GridSizeField} from "../components/3d/controlFields/GridSizeField";

export default function ObjectDetail() {
  const {state, dispatch} = useContext(DataStoreContext);
  const urlParams = useParams<{ [OPERATING_ENVIRONMENT_URLS.OBJECT_DETAIL.ARG]?: string }>();
  const three = useThree();
  const {controlState, setControlState} = useControlState();
  const {data, status, fetchAll} = useObject({objectId: urlParams.objectId ?? ''});
  useFetchAllQueryHelper({
    onChange: (newData, newStatus) => {
      dispatch({
        type: CONTEXT_ACTION_TYPE.OBJECT_DETAIL,
        payload: {
          object: Object.values(newData.find(() => true)?.object ?? {}).find(() => true) ?? null,
          searchStatus: newStatus
        } satisfies ObjectDetailPayload
      });
    },
    data,
    status
  })

  useEffect(() => {
    if (urlParams.objectId) {
      fetchAll().catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.objectId]);

  return (
    <Container maxWidth="lg" sx={{maxWidth: "1500px !important", mt: 2}}>
      <Card sx={{p: 3, mt: 2}}>
        <Stack gap={4}>
          <Box display='flex' gap={4} flexWrap='wrap'>
            <Stack gap={1}>
              <Typography variant="body2">※ モデルはイメージです。実際とは異なる場合があります。</Typography>
              <View3D
                three={three}
                url={state.objectDetail.object?.url.glb.original.find(() => true)}
                showPerspectiveControl
                showLookAtControl
                showGridSizeControl
                options={{
                  displayControlPrimaryDirection: true,
                  displayControlSecondaryDirection: true,
                }}
              />
            </Stack>

            <Stack gap={4}>
              <TableContainer component={Paper}>
                <Table sx={{maxWidth: "100%"}} size="small" aria-label="a dense table">
                  <TableHead sx={{bgcolor: "#EEE"}}>
                    <TableRow>
                      <TableCell sx={{px: 1, py: 0.5}}>属性</TableCell>
                      <TableCell sx={{px: 1, py: 0.5}}>属性値</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                      <TableCell sx={{px: 1, py: 0.5, textWrap: 'nowrap'}}>パッケージ名</TableCell>
                      <TableCell
                        sx={{px: 1, py: 0.5, overflowWrap: 'anywhere'}}
                        dangerouslySetInnerHTML={{__html: String(state.objectDetail.object?.attr['object-pkg-original-name'] ?? '')}}
                      />
                    </TableRow>
                    <TableRow sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                      <TableCell sx={{px: 1, py: 0.5, textWrap: 'nowrap'}}>オブジェクト名</TableCell>
                      <TableCell
                        sx={{px: 1, py: 0.5, overflowWrap: 'anywhere'}}
                        dangerouslySetInnerHTML={{__html: String(state.objectDetail.object?.attr.original_name ?? '')}}
                      />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack gap={2}>
                <MaterialTypeField
                  control={three.control}
                  value={controlState.materialType}
                  onChange={(e) => handleInputChange<ControlState>(e, controlState, setControlState)}
                />
                <PerspectiveField
                  control={three.control}
                  value={controlState.perspective}
                  onChange={(e) => handleInputChange<ControlState>(e, controlState, setControlState)}
                />
                <LookAtField
                  control={three.control}
                  value={controlState.lookAtType}
                  onChange={(e) => handleInputChange<ControlState>(e, controlState, setControlState)}
                />
                <GridSizeField
                  control={three.control}
                  value={controlState.gridSize}
                  onChange={(e) => handleInputChange<ControlState>(e, controlState, setControlState)}
                />
              </Stack>
            </Stack>
          </Box>

          <Box display='flex' justifyContent='center' flexWrap='wrap' width='100%' gap={4}>
            <DownloadObjectFileButton obj={state.objectDetail.object}/>
          </Box>
        </Stack>
      </Card>
    </Container>
  );
}

function useObject(params: { objectId: string }) {
  return useFetchAllQuery({
    queryFn: (query) => findObject({
      body: {
        'query-attr': {
          'object-id': {
            $in: [params.objectId],
          }
        },
      },
      query
    }),
    needToFetchMore: () => false
  });
}
